// Image caching service to avoid repeated API requests
class ImageCache {
  private cache = new Map<string, string>();
  private failedImages = new Set<string>();
  private loadingPromises = new Map<string, Promise<string>>();

  // Cache duration in milliseconds (24 hours)
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000;
  private cacheTimestamps = new Map<string, number>();

  // Fallback images for different content types
  private readonly FALLBACK_IMAGES = {
    movie: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+TW92aWU8L3RleHQ+Cjwvc3ZnPgo=',
    series: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U2VyaWVzPC90ZXh0Pgo8L3N2Zz4K',
    anime: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+QW5pbWU8L3RleHQ+Cjwvc3ZnPgo=',
    book: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Qm9vazwvdGV4dD4KPC9zdmc+Cg==',
    comic: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q29taWM8L3RleHQ+Cjwvc3ZnPgo=',
    creator: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM3MTgwOTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkNyZWF0b3I8L3RleHQ+Cjwvc3ZnPgo=',
    default: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzQyIiBoZWlnaHQ9IjUxMyIgdmlld0JveD0iMCAwIDM0MiA1MTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzNDIiIGhlaWdodD0iNTEzIiBmaWxsPSIjMUEyMDJDIi8+Cjx0ZXh0IHg9IjE3MSIgeT0iMjU2LjUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzcxODA5NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q29udGVudDwvdGV4dD4KPC9zdmc+Cg=='
  };

  /**
   * Get cached image URL or fetch and cache it
   */
  async getImageUrl(imagePath: string, size: string = 'w500', fallbackType: string = 'default'): Promise<string> {
    const cacheKey = `${imagePath}_${size}`;
    
    // Return cached image if available and not expired
    if (this.cache.has(cacheKey) && !this.isExpired(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Return fallback if image previously failed
    if (this.failedImages.has(cacheKey)) {
      return this.getFallbackImage(fallbackType);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }

    // Start loading the image
    const loadingPromise = this.loadImage(imagePath, size, fallbackType, cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      this.loadingPromises.delete(cacheKey);
      return result;
    } catch (error) {
      this.loadingPromises.delete(cacheKey);
      return this.getFallbackImage(fallbackType);
    }
  }

  /**
   * Load and cache an image
   */
  private async loadImage(imagePath: string, size: string, fallbackType: string, cacheKey: string): Promise<string> {
    if (!imagePath) {
      return this.getFallbackImage(fallbackType);
    }

    // Check if it's a Firebase Storage URL or external URL
    let imageUrl: string;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      // It's already a full URL (Firebase Storage or external)
      imageUrl = imagePath;
    } else {
      // It's a TMDB path, construct the URL
      imageUrl = `https://image.tmdb.org/t/p/${size}${imagePath}`;
    }
    
    try {
      // Preload the image to check if it exists
      await this.preloadImage(imageUrl);
      
      // Cache the successful image
      this.cache.set(cacheKey, imageUrl);
      this.cacheTimestamps.set(cacheKey, Date.now());
      
      return imageUrl;
    } catch (error) {
      console.error(`Failed to load image: ${imageUrl}`, error);
      
      // Mark as failed to avoid retrying
      this.failedImages.add(cacheKey);
      
      // Return fallback image
      return this.getFallbackImage(fallbackType);
    }
  }

  /**
   * Preload an image to check if it exists
   */
  private preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }

  /**
   * Get fallback image based on content type
   */
  private getFallbackImage(type: string): string {
    return this.FALLBACK_IMAGES[type as keyof typeof this.FALLBACK_IMAGES] || this.FALLBACK_IMAGES.default;
  }

  /**
   * Check if cached image is expired
   */
  private isExpired(cacheKey: string): boolean {
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp) return true;
    
    return Date.now() - timestamp > this.CACHE_DURATION;
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cacheTimestamps.forEach((timestamp, key) => {
      if (now - timestamp > this.CACHE_DURATION) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
      this.failedImages.delete(key);
    });
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.failedImages.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { cached: number; failed: number; loading: number } {
    return {
      cached: this.cache.size,
      failed: this.failedImages.size,
      loading: this.loadingPromises.size
    };
  }
}

// Create singleton instance
export const imageCache = new ImageCache();

// Clear expired cache every hour
setInterval(() => {
  imageCache.clearExpiredCache();
}, 60 * 60 * 1000);

export default imageCache;
